import type { ReactNode } from "react";
import { Platform, ScrollView, StyleSheet, View } from "react-native";

import {
  AppLink,
  BrandMark,
  PageTitle,
  Screen,
  WebFooter,
} from "~/components/ui";
import { WebStoreBadges } from "~/components/composed/WebStoreBadges";
import {
  radii,
  shadows,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { getSiteUrl } from "~/utils/runtime-config";

export function AuthScreen({
  children,
  title,
  subtitle,
}: {
  children: ReactNode;
  title: string;
  subtitle?: string;
}) {
  const stylesheet = useThemedStyles(makeStyles);
  const siteUrl = getSiteUrl();

  return (
    <Screen style={stylesheet.screen}>
      <ScrollView contentContainerStyle={stylesheet.scroll}>
        <View style={stylesheet.body}>
          <View style={stylesheet.content}>
            <View style={stylesheet.brand}>
              <BrandMark size="xl" />
            </View>
            <View style={stylesheet.card}>
              <PageTitle title={title} subtitle={subtitle} />
              {children}
            </View>
            {siteUrl && Platform.OS === "web" ? (
              <View style={stylesheet.backToWebsite}>
                <AppLink href={siteUrl as never} size="sm">
                  ← Back to website
                </AppLink>
              </View>
            ) : null}
          </View>
        </View>
        <WebStoreBadges />
        <WebFooter />
      </ScrollView>
    </Screen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    screen: { backgroundColor: colors.background },
    scroll: { flexGrow: 1 },
    body: {
      flexGrow: 1,
      width: "100%",
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.l,
    },
    content: {
      width: "100%",
      maxWidth: 450,
      gap: spacing.l,
    },
    brand: {
      paddingHorizontal: spacing.xl,
      display: "flex",
      flexDirection: "row",
      justifyContent: "center",
    },
    backToWebsite: { alignItems: "center" },
    card: {
      width: "100%",
      padding: spacing.xl,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: radii.card,
      backgroundColor: colors.elevated,
      gap: spacing.l,
      ...shadows.sm,
    },
  });
